import {useOrganizationService} from "../../../../services";
import {useJwt} from "../../../../helpers/common";
import React, {useEffect, useState, useMemo} from "react";
import {useSelector} from 'react-redux';
import {HiLink} from "react-icons/hi";
import toast from 'react-hot-toast';
import CustomButton from '../../../../components/common/CustomButton';
import FormInput from '../../../../components/common/FormInput';
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import useIntegrationStatus from "../../../../hooks/useIntegrationStatus";
import AsanaSettingsSkeleton from "./AsanaSettingsSkeleton";
import UnsavedChangesIndicator from "../../../../components/common/UnsavedChangesIndicator";

interface AsanaSettingsProps {
  handleClose: () => void;
  handleActivationCallback?: () => void;
}

const AsanaSettings: React.FC<AsanaSettingsProps> = ({ handleClose, handleActivationCallback }) => {
  const [payload, setPayload] = useState({
    access_token: undefined,
    workspace_id: undefined,
    project_id: '',
    status_stages: '',
    is_active: false
  });
  const JWTdata:any = useJwt();
  const org_id = JWTdata.org_id;
  const {asanaSettingsLoading,asanaSettings} = useSelector((state: any) => state.organization);
  const { saveAsanaSettings, getAsanaSettings } = useOrganizationService();
  const { isAnyOtherIntegrationActive } = useIntegrationStatus();

  useEffect(()=>{
    if(org_id){
      getAsanaSettings(org_id)
    }
  },[org_id]);

  useEffect(() => {
    if(!asanaSettingsLoading && asanaSettings) {
      if(!payload.access_token && !payload.workspace_id) {
        setPayload(asanaSettings)
      }
    }
  }, [asanaSettingsLoading]);

  const hasUnsavedChanges = useMemo(() => {
    if (!asanaSettings || asanaSettingsLoading) {
      return false;
    }

    const normalizeString = (val: any) => (val === null || val === undefined ? '' : String(val).trim());
    const normalizeBool = (val: any) => Boolean(val);

    return (
      normalizeString(payload?.access_token) !== normalizeString(asanaSettings?.access_token) ||
      normalizeString(payload?.workspace_id) !== normalizeString(asanaSettings?.workspace_id) ||
      normalizeString(payload?.project_id) !== normalizeString(asanaSettings?.project_id) ||
      normalizeString(payload?.status_stages) !== normalizeString(asanaSettings?.status_stages) ||
      normalizeBool(payload?.is_active) !== normalizeBool(asanaSettings?.is_active)
    );
  }, [payload, asanaSettings, asanaSettingsLoading]);

  const handleOnChange= (e:any)=> {
    const {name,value} = e.target;
    setPayload({...payload,[name]:value})
  }

  const handleCheckbox= (e:any)=> {
    const {checked} = e.target;
    if(checked) {
      setPayload({...payload,is_active:true})
    } else {
      setPayload({...payload,is_active:false})
    }
  }

  const onSubmit = () => {
    if(!payload?.access_token){
      toast.error("Please add access token", {duration:2000});
      return;
    }
    if(!payload?.workspace_id){
      toast.error("Please add workspace id!", {duration:2000});
      return;
    }
    if(!payload?.project_id){
      toast.error("Please add project id!", {duration:2000});
      return;
    }
    // if(isAnyOtherIntegrationActive("asana")){
    //   toast.error("Only one integration can be active at a time.", {duration:2000});
    //   return;
    // }

    saveAsanaSettings(org_id,{...payload})
      .then(() => {
        handleActivationCallback?.();
      })
  }

  return (
    <div className="row bg-tertiary-light dark:bg-tertiary-dark w-full h-full relative flex flex-col">
    

      {/* Header */}
      <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5
      dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col min-w-0 max-w-full">
            <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
              Asana Settings
            </div>
            <a
              href="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/documents/Cadence+-+Asana+Setup+Guide.pdf"
              target="_blank"
              rel="noreferrer"
              className="flex flex-row items-center gap-2 mt-2 text-light-text-tertiary dark:text-white/60 text-sm hover:text-sky-500 dark:hover:text-sky-200"
            >
              <HiLink className="w-4 h-4"/>
              Visit Cadence-Asana Setup guide
            </a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white
              hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
              onClick={handleClose}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {asanaSettingsLoading ? (
        <AsanaSettingsSkeleton />
      ) : (
        <div className="px-8 py-12 flex-1">
        <FormInput
          id="access_token"
          name="access_token"
          label={
            <span className="flex">
              Access Token
              <span className="ml-1">*</span>
            </span>
          }
          type="password"
          value={payload?.access_token || ''}
          onChange={handleOnChange}
          placeholder="Enter access token"
        />
        <FormInput
          id="workspace_id"
          name="workspace_id"
          label={
            <span className="flex">
              Workspace ID
              <span className="ml-1">*</span>
            </span>
          }
          type="text"
          value={payload?.workspace_id || ''}
          onChange={handleOnChange}
          placeholder="Workspace Id"
        />
        <FormInput
          id="project_id"
          name="project_id"
          label={
            <span className="flex">
              Project ID
              <span className="ml-1">*</span>
            </span>
          }
          type="text"
          value={payload?.project_id || ''}
          onChange={handleOnChange}
          placeholder="Project ID"
        />
        <FormInput
          id="status_stages"
          name="status_stages"
          label="Swimlanes"
          type="textarea"
          value={payload?.status_stages || ''}
          onChange={handleOnChange}
          placeholder='{
            "In QA": "25%",
            "QA Tested": "50%",
            "In Prod": "100%"
          }'
          className="w-full h-40 block p-2 disabled:pointer-events-none text-gray-900 border border-gray-300 bg-gray-50 sm:text-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          maxLength={1000}
        />

        <ToggleSwitch
          id="is_active"
          name="is_active"
          checked={payload?.is_active}
          onChange={handleCheckbox}
          label="Activate Asana Integration in Cadence Pro"
        />
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700
        bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg sticky bottom-0">
        <CustomButton
          variant="cancel"
          onClick={handleClose}
          className="ml-2"
        >
          Cancel
        </CustomButton>

        <div className="ml-auto flex items-center gap-3">
          <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
          <CustomButton
            onClick={onSubmit}
            disabled={
              asanaSettingsLoading
              || !payload.access_token
              || !payload.project_id
              || !payload.workspace_id
            }
            title={isAnyOtherIntegrationActive('asana') ? "Only one integration can be active at a time." : ""}
            isLoading={asanaSettingsLoading}
            size="lg"
          >
            {!asanaSettingsLoading ? 'Save' : "Saving..."}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}


export default AsanaSettings;